// utils/schema/eventSchema.js
import dayjs from "dayjs";

export const toIso = (d) => {
  if (!d) return null;
  const x = dayjs(d);
  return x.isValid() ? x.toISOString() : null;
};

export const absUrl = (baseUrl, pathOrUrl) => {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${baseUrl}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
};

export const cleanText = (v) =>
  (v || "")
    .toString()
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const pickName = (e) => (e.title_when || e.title || "").trim();

export const pickDescription = (e) =>
  cleanText(e.description_meta_when || e.description_meta || e.description_when || e.description || "");

export const pickImage = (e, baseUrl) =>
  absUrl(baseUrl, e.img_cover || e.img || null);

export const pickOrganizer = (e) => {
  const name =
    (e.event_name_organizer_event || "").trim() ||
    (e.place_name_organizer || "").trim();

  const url =
    (e.event_url_organizer_event || "").trim() ||
    (e.place_url_organizer || "").trim();

  if (!name && !url) return null;

  const obj = { "@type": "Organization" };
  if (name) obj.name = name;
  if (url) obj.url = url;

  return obj;
};

export const pickPerformer = (e) => {
  const p = (e.place_performer || "").trim();
  if (!p) return null;

  // Ako je performer osoba, promeni "@type" u "Person"
  return { "@type": "PerformingGroup", name: p };
};

// Single Event JSON-LD (Event)
export const buildEventJsonLd = ({ event, baseUrl, urlPath }) => {
  const fullUrl = `${baseUrl}${urlPath}`;

  const startDate = toIso(event.start_date || event.start_date_convert);
  const endDate = toIso(event.end_date || event.end_date_convert);
  const image = pickImage(event, baseUrl);

  const json = {
    "@context": "https://schema.org",
    "@type": "Event",
    "@id": `${fullUrl}#event`,
    "name": pickName(event),
    "url": fullUrl,
    "startDate": startDate,
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "eventStatus": "https://schema.org/EventScheduled",
    "location": {
      "@type": "Place",
      "name": event.place_name,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": event.place_street,
        "addressLocality": event.place_address,
        "postalCode": event.place_postal,
        "addressRegion": event.place_address_region,
        "addressCountry": event.place_address_country,
      },
    },
    "description": pickDescription(event),
  };

  if (endDate) json.endDate = endDate;
  if (image) json.image = [image];

  const organizer = pickOrganizer(event);
  if (organizer) json.organizer = organizer;

  const performer = pickPerformer(event);
  if (performer) json.performer = performer;

  // OFFERS (RSD uvek) - dodaj samo ako postoji price
  const priceRaw = event.price;
  const priceStr = priceRaw === null || priceRaw === undefined ? "" : String(priceRaw).trim();

  if (priceStr !== "") {
    const isFree =
      priceStr === "0" ||
      priceStr.toLowerCase() === "besplatno" ||
      priceStr.toLowerCase() === "free";

    json.offers = {
      "@type": "Offer",
      "url": fullUrl,
      "availability": "https://schema.org/InStock",
      "validFrom": startDate || toIso(event.start_date_convert),
      "priceCurrency": "RSD",
      "price": isFree ? "0" : priceStr,
    };

    if (isFree) json.isAccessibleForFree = true;
  }

  return json;
};

// List JSON-LD (ItemList) - svaki event dobija svoj SINGLE URL preko eventUrlFn(event)
export const buildEventListJsonLd = ({ events, eventUrlFn }) => {
  const list = (events || []).map((event, i) => {
    const singleUrl = eventUrlFn(event);

    // Kreiramo base obj, pa prepišemo url + @id da bude single url
    const obj = buildEventJsonLd({
      event,
      baseUrl: "https://pijace.com",
      urlPath: "", // placeholder, jer url prepisujemo dole
    });

    obj.url = singleUrl;
    obj["@id"] = `${singleUrl}#event`;
    obj.position = i + 1;

    return obj;
  });

  return {
    "@context": "http://schema.org",
    "@type": "ItemList",
    "numberOfItems": String(events?.length || 0),
    "itemListOrder": "Descending",
    "itemListElement": list,
  };
};
